class DiscountPopup extends HTMLElement {
  constructor() {
    super();
    this.initializeProperties();
    this.cacheElements();
    this.bindMethods();
    this.init();
  }

  initializeProperties() {
    this.CONFIG = {
      POPUP_DELAY_TIME: 26000,
      POPUP_DELAY_TIME_ADD: 5000,
      POPUP_DELAY_SCROLL: 10,
      SCROLL_THROTTLE_DELAY: 100,
      CART_ACTIVE_DEBOUNCE: 800
    };

    this.state = {
      popupShown: false,
      scrolledMoreThanDistance: false,
      pageLoadedMoreThanTime: false,
      lastActiveTime: 0,
      isInitialized: false
    };

    this.settings = {
      maxShowTimes: parseInt(this.getAttribute('data-show-discount'), 10) || 1,
      popupTrigger: this.getAttribute('data-popup-trigger') || 'page_load'
    };

    this.persistence = {
      closeCount: Number(sessionStorage.getItem('discountPopupCloseCount')) || 0,
      formSubmitted: localStorage.getItem('discountFormSubmitted') === 'true'
    };

    this.previousActiveElement = null;
  }

  cacheElements() {
    const popupSection = this.closest('.section-discount-popup');

    this.elements = {
      popup: this,
      popupSection,
      closeButton: this.querySelector('.discount-close-popup'),
      form: this.querySelector('#discount-block-form'),
      discountCodeInput: this.querySelector('#discountCode'),
      copyButton: this.querySelector('.copy-discount-button'),
      overlay: popupSection ? popupSection.querySelector('.discount-block-overlay') : null,
      cartDrawer: null
    };
  }

  bindMethods() {
    this.handleScroll = this.throttle(this.checkScroll.bind(this), this.CONFIG.SCROLL_THROTTLE_DELAY);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleCloseKeydown = this.handleCloseKeydown.bind(this);
    this.handleCopyClick = this.copyDiscountCode.bind(this);
    this.handleCopyKeydown = this.handleCopyKeydown.bind(this);
    this.handleInputKeydown = this.handleInputKeydown.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleCloseClick = () => this.hidePopup(false);
    this.handlePageLoadTime = this.onPageLoadedTime.bind(this);
  }

  init() {
    if (!this.elements.popupSection || 
        this.persistence.closeCount >= this.settings.maxShowTimes || 
        this.persistence.formSubmitted ||
        this.state.isInitialized) {
      return;
    }

    try {
      this.setupEventListeners();
      this.initializeTriggerBehavior();
      this.state.isInitialized = true;
    } catch (error) {
      console.error('DiscountPopup initialization failed:', error);
    }
  }

  setupEventListeners() {
    if (this.elements.closeButton) {
      this.elements.closeButton.addEventListener('click', this.handleCloseClick);
      this.elements.closeButton.addEventListener('keydown', this.handleCloseKeydown);
    }
  
    if (this.elements.overlay) {
      this.elements.overlay.addEventListener('click', this.handleCloseClick);
    }
  
    if (this.elements.copyButton) {
      this.elements.copyButton.addEventListener('click', this.handleCopyClick);
      this.elements.copyButton.addEventListener('keydown', this.handleCopyKeydown);
    }
  
    if (this.elements.discountCodeInput) {
      this.elements.discountCodeInput.addEventListener('keydown', this.handleInputKeydown);
    }
  
    document.addEventListener('keydown', this.handleKeydown);
  
    if (this.elements.form) {
      this.elements.form.addEventListener('submit', this.handleFormSubmit);
    }
  }

  initializeTriggerBehavior() {
    switch (this.settings.popupTrigger) {
      case 'page_load':
        this.setupPageLoadTrigger();
        break;
      case 'product_added':
      case 'product_added_each':
        this.setupCartTrigger();
        break;
      default:
        console.warn(`Unknown popup trigger: ${this.settings.popupTrigger}`);
    }
  }

  setupPageLoadTrigger() {
    this.setupScrollListener();
    setTimeout(this.handlePageLoadTime, this.CONFIG.POPUP_DELAY_TIME);
  }

  setupScrollListener() {
    const initialScrollPercentage = this.calculateScrollPercentage();
    
    if (initialScrollPercentage > this.CONFIG.POPUP_DELAY_SCROLL) {
      this.state.scrolledMoreThanDistance = true;
      if (!this.state.pageLoadedMoreThanTime) {
        setTimeout(() => this.showPopup(), this.CONFIG.POPUP_DELAY_TIME);
      }
    } else {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }
  }

  setupCartTrigger() {
    this.elements.cartDrawer = document.querySelector('cart-drawer.drawer');
    
    if (!this.elements.cartDrawer) {
      console.warn('Cart drawer not found for popup trigger');
      return;
    }

    this.observeCartDrawer();
  }

  calculateScrollPercentage() {
    const windowHeight = window.innerHeight;
    const scrollTop = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight;
    
    return ((scrollTop + windowHeight) / documentHeight) * 100;
  }

  checkScroll() {
    const scrollPercentage = this.calculateScrollPercentage();
    
    if (scrollPercentage > this.CONFIG.POPUP_DELAY_SCROLL) {
      this.state.scrolledMoreThanDistance = true;
      this.removeScrollListener();
      
      if (this.state.pageLoadedMoreThanTime) {
        this.showPopup();
      } else {
        setTimeout(() => this.showPopup(), this.CONFIG.POPUP_DELAY_TIME);
      }
    }
  }

  onPageLoadedTime() {
    this.state.pageLoadedMoreThanTime = true;
    if (this.state.scrolledMoreThanDistance) {
      this.showPopup();
    }
  }

  showPopup() {
    
    if (this.persistence.closeCount <= this.settings.maxShowTimes &&
        this.settings.popupTrigger === 'product_added_each') {
      this.state.popupShown = false;
    }
  
    if (this.state.popupShown || !this.elements.popupSection) return;
  
    this.previousActiveElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
  
    this.elements.popupSection.classList.remove('popup-hidden');
    this.elements.popupSection.classList.add('popup-visible');
    this.state.popupShown = true;
  
    this.manageFocus();
  
    this.dispatchEvent(new CustomEvent('popup:shown', {
      detail: { trigger: this.settings.popupTrigger }
    }));
  }

  hidePopup(fromKeyboard = false) {
    if (!this.elements.popupSection) return;
  
    this.elements.popupSection.classList.remove('popup-visible');
    this.elements.popupSection.classList.add('popup-hidden');
  
    this.persistence.closeCount++;
    sessionStorage.setItem('discountPopupCloseCount', this.persistence.closeCount);
  
    this.dispatchEvent(new CustomEvent('popup:hidden', {
      detail: { closeCount: this.persistence.closeCount }
    }));
  
    this.restoreFocus(fromKeyboard);
  }

  observeCartDrawer() {
    if (!this.elements.cartDrawer) return;

    const observer = new MutationObserver((mutations) => {
      try {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            this.handleCartStateChange();
          }
        });
      } catch (error) {
        console.error('Cart observer error:', error);
      }
    });

    observer.observe(this.elements.cartDrawer, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class']
    });

    this.cartObserver = observer;
  }

  handleCartStateChange() {
    const isActive = this.elements.cartDrawer?.classList.contains('active');
    const now = Date.now();

    if (isActive && (now - this.state.lastActiveTime > this.CONFIG.CART_ACTIVE_DEBOUNCE)) {
      this.state.lastActiveTime = now;
      this.handleCartActivation();
    }
  }

  handleCartActivation() {
    if (this.settings.popupTrigger === 'product_added') {
      if (!sessionStorage.getItem('discountPopupShown')) {
        setTimeout(() => {
          this.showPopup();
          sessionStorage.setItem('discountPopupShown', 'true');
        }, this.CONFIG.POPUP_DELAY_TIME_ADD);
      }
    } else if (this.settings.popupTrigger === 'product_added_each') {
      setTimeout(() => this.showPopup(), this.CONFIG.POPUP_DELAY_TIME_ADD);
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.state.popupShown) {
      event.preventDefault();
      this.hidePopup(true);
    }
  }
  
  handleCloseKeydown(event) {
    const isEnter = event.key === 'Enter';
    const isSpace = event.key === ' ' || event.key === 'Spacebar';
    const isEscape = event.key === 'Escape' || event.key === 'Esc';
    if (isEnter || isSpace || isEscape) {
      event.preventDefault();
      this.hidePopup(true);
    }
  }

  handleFormSubmit() {
    localStorage.setItem('discountFormSubmitted', 'true');
    this.persistence.formSubmitted = true;
  }

  async copyDiscountCode() {
    if (!this.elements.discountCodeInput) return;

    const textToCopy = this.elements.discountCodeInput.value;
    
    try {
      // Modern clipboard API (preferred method)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        this.showCopySuccess();
        return;
      }
      
      // Fallback: select text for manual copy
      this.selectTextForManualCopy();
      
    } catch (error) {
      console.error('Clipboard API failed:', error);
      // Fallback: select text for manual copy
      this.selectTextForManualCopy();
    }
  }

  selectTextForManualCopy() {
    try {
      // Select the text so user can manually copy with Ctrl+C
      this.elements.discountCodeInput.focus();
      this.elements.discountCodeInput.select();
      this.elements.discountCodeInput.setSelectionRange(0, 99999);
      
      // Show a message indicating manual copy is needed
      this.showManualCopyMessage();
      
    } catch (error) {
      console.error('Text selection failed:', error);
    }
  }

  showCopySuccess() {
    if (this.elements.discountCodeInput) {
      this.elements.discountCodeInput.classList.add('discount-code-success');
      
      // Remove success class after 3 seconds
      setTimeout(() => {
        if (this.elements.discountCodeInput) {
          this.elements.discountCodeInput.classList.remove('discount-code-success');
        }
      }, 3000);
    }
  }

  showManualCopyMessage() {
    if (this.elements.discountCodeInput) {
      // Add a class to show manual copy message
      this.elements.discountCodeInput.classList.add('discount-code-manual-copy');
      
      // Remove manual copy class after 5 seconds (longer than success message)
      setTimeout(() => {
        if (this.elements.discountCodeInput) {
          this.elements.discountCodeInput.classList.remove('discount-code-manual-copy');
        }
      }, 5000);
    }
  }

  handleCopyKeydown(event) {
    const isEnter = event.key === 'Enter';
    const isSpace = event.key === ' ' || event.key === 'Spacebar';
    if (isEnter || isSpace) {
      event.preventDefault();
      this.copyDiscountCode();
    }
  }

  handleInputKeydown(event) {
    const isEnter = event.key === 'Enter';
    const isSpace = event.key === ' ' || event.key === 'Spacebar';
    if (isEnter || isSpace) {
      event.preventDefault();
      this.copyDiscountCode();
    }
  }

  manageFocus() {
    const focusContainer = this.elements.popup;
    if (!focusContainer) return;
  
    focusContainer.setAttribute('tabindex', '-1');
  
    const initialFocus =
      this.elements.closeButton ||
      this.elements.discountCodeInput ||
      this.elements.copyButton ||
      focusContainer;
  
    setTimeout(() => {
      if (typeof trapFocus === 'function') {
        trapFocus(focusContainer, initialFocus);
      }
      if (initialFocus && typeof initialFocus.focus === 'function') {
        initialFocus.focus();
      }
    }, 50);
  }

  restoreFocus(fromKeyboard) {
    const elementToFocus =
      fromKeyboard &&
      this.previousActiveElement &&
      document.contains(this.previousActiveElement)
        ? this.previousActiveElement
        : null;
  
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(elementToFocus || null);
    } else if (elementToFocus && typeof elementToFocus.focus === 'function') {
      setTimeout(() => {
        if (document.contains(elementToFocus)) {
          elementToFocus.focus();
        }
      }, 50);
    }
  
    this.previousActiveElement = null;
  }

  removeScrollListener() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  throttle(func, delay) {
    let timeoutId;
    let lastExecTime = 0;
    
    return function (...args) {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this.elements.closeButton) {
      this.elements.closeButton.removeEventListener('click', this.handleCloseClick);
      this.elements.closeButton.removeEventListener('keydown', this.handleCloseKeydown);
    }
    
    if (this.elements.copyButton) {
      this.elements.copyButton.removeEventListener('click', this.handleCopyClick);
      this.elements.copyButton.removeEventListener('keydown', this.handleCopyKeydown);
    }

    if (this.elements.discountCodeInput) {
      this.elements.discountCodeInput.removeEventListener('keydown', this.handleInputKeydown);
    }
    
    document.removeEventListener('keydown', this.handleKeydown);
    
    if (this.elements.form) {
      this.elements.form.removeEventListener('submit', this.handleFormSubmit);
    }
    
    this.removeScrollListener();
    
    if (this.cartObserver) {
      this.cartObserver.disconnect();
      this.cartObserver = null;
    }
    
    this.state.isInitialized = false;
  }
}

customElements.define('discount-popup', DiscountPopup);